import type {
  IngestMappingConfigSummary,
  TabularConfigBuildDetail,
  TabularConfigDraft
} from '@axilon-apps/domains-ontology-ui-state'
import { tabularConfigBuildPath, type TabularConfigTab } from '../../TabularIngestJobPage/route'

/** The page's tab slugs are the route's `tab` query values. */
export type ConfigTab = TabularConfigTab

const SCRIPT_TABS: ConfigTab[] = [
  'tables',
  'checks',
  'description',
  'script',
  'fingerprint',
  'history'
]
const AGENT_TABS: ConfigTab[] = ['tables', 'checks', 'description', 'pages', 'history']

/** What a saved version's page says about a build besides the version itself. */
export type ViewBanner =
  | { kind: 'draft_ready'; build: TabularConfigBuildDetail }
  | { kind: 'building'; build: TabularConfigBuildDetail }
  /** The draft's approval is being committed. */
  | { kind: 'saving'; build: TabularConfigBuildDetail }
  | {
      kind: 'draft_saved'
      build: TabularConfigBuildDetail
      version: number
      by: string
      at: string
      /** The run that ingests the build's uploaded examples, started by the save — or skipped by choice. */
      exampleIngest?: { jobIds: string[]; error?: string; skipped?: boolean }
    }
  | {
      kind: 'draft_rejected'
      build: TabularConfigBuildDetail
      by: string
      at: string
      note: string
    }
  /** A reviewer sent the draft back; `supersededBy` is the build that took over. */
  | {
      kind: 'draft_superseded'
      build: TabularConfigBuildDetail
      by: string
      at: string
      note: string
      supersededBy?: string
    }

export type BuildOutcome = 'failed' | 'stopped' | 'rejected' | 'superseded'

export type ConfigView =
  | {
      kind: 'version'
      config: IngestMappingConfigSummary
      /** The newest active row of the name — edits always start from it. */
      latest: IngestMappingConfigSummary | undefined
      banner?: ViewBanner
    }
  | {
      kind: 'draft'
      build: TabularConfigBuildDetail
      draft: TabularConfigDraft
      /** The version the draft updates (update mode); absent for a first build. */
      base?: IngestMappingConfigSummary
      /** An approval is being committed: the draft stays on screen, read-only, until it is a version. */
      saving?: boolean
    }
  | { kind: 'building'; build: TabularConfigBuildDetail }
  /** A build that ended without a version to show: failed, stopped, or a rejected first draft. */
  | { kind: 'ended'; build: TabularConfigBuildDetail; outcome: BuildOutcome }
  /** `?version=draft` on the config route while a build is in flight: the build has its own page. */
  | { kind: 'redirect'; to: string }
  | { kind: 'empty' }

export interface ResolveConfigViewInput {
  name: string
  /** Every row of the name, any state. */
  versions: IngestMappingConfigSummary[]
  /** The build named in the URL (`/builds/:buildJobId`), once loaded. */
  build: TabularConfigBuildDetail | null
  /** The one build in flight for the name, on the config route. */
  active: TabularConfigBuildDetail | null
  /** The newest build in any state — only fetched for `?version=draft` with nothing active. */
  latestBuild: TabularConfigBuildDetail | null
  /** `?version=` as written. */
  requestedVersion: string | null
}

export function activeVersions(
  versions: IngestMappingConfigSummary[]
): IngestMappingConfigSummary[] {
  return versions.filter((row) => row.state === 'active')
}

export function newestFirst(versions: IngestMappingConfigSummary[]): IngestMappingConfigSummary[] {
  return [...versions].sort((a, b) => b.version - a.version)
}

export function latestActiveVersion(
  versions: IngestMappingConfigSummary[]
): IngestMappingConfigSummary | undefined {
  return newestFirst(activeVersions(versions))[0]
}

/**
 * Archived rows to restore, newest first.
 *
 * A pinned `scope.version` restores only that archived row, so an older
 * version can come back while a newer active one stays current. With no pin,
 * a name that still has an active version restores nothing; a fully archived
 * name restores every archived version so `@latest` intakes work again.
 */
export function versionsToUnarchive(
  versions: IngestMappingConfigSummary[],
  scope: { version?: number } = {}
): IngestMappingConfigSummary[] {
  const archived = newestFirst(versions.filter((row) => row.state === 'archived'))
  if (scope.version !== undefined) return archived.filter((row) => row.version === scope.version)
  if (versions.some((row) => row.state === 'active')) return []
  return archived
}

/**
 * The file types among `extensions` that a version's `input.accept` leaves
 * out, so a run fails on their files. Without `input.accept` it takes every type.
 */
export function refusedExtensions(
  accept: readonly string[] | undefined,
  extensions: readonly string[]
): string[] {
  if (!accept) return []
  const accepted = new Set(accept.map((extension) => extension.toLowerCase()))
  return extensions.filter((extension) => !accepted.has(extension.toLowerCase()))
}

/** File types in a sentence: ".xlsm, .xls and .csv". */
export function extensionList(extensions: readonly string[]): string {
  if (extensions.length < 2) return extensions.join('')
  return `${extensions.slice(0, -1).join(', ')} and ${extensions[extensions.length - 1]}`
}

export function isBuildInFlight(build: TabularConfigBuildDetail): boolean {
  return (
    !build.completedAt &&
    build.state !== 'cancelled' &&
    build.state !== 'error' &&
    build.state !== 'done'
  )
}

/**
 * One decision for the whole page. The build route shows the build named in
 * the URL whatever became of it; the config route shows a version and, when
 * a build is in flight, points at it.
 */
export function resolveConfigView(input: ResolveConfigViewInput): ConfigView {
  const { versions } = input
  const latest = latestActiveVersion(versions) ?? newestFirst(versions)[0]
  if (input.build) return viewForBuild(input.build, versions, latest)

  if (input.requestedVersion === 'draft') {
    if (input.active)
      return { kind: 'redirect', to: tabularConfigBuildPath(input.name, input.active.jobId) }
    if (input.latestBuild) {
      if (isBuildInFlight(input.latestBuild)) {
        return { kind: 'redirect', to: tabularConfigBuildPath(input.name, input.latestBuild.jobId) }
      }
      return viewForBuild(input.latestBuild, versions, latest)
    }
    return latest ? { kind: 'version', config: latest, latest } : { kind: 'empty' }
  }

  const banner = bannerForActive(input.active)
  const requested = Number(input.requestedVersion)
  const picked =
    input.requestedVersion !== null && Number.isSafeInteger(requested)
      ? versions.find((row) => row.version === requested)
      : undefined
  const config = picked ?? latest
  if (config) return { kind: 'version', config, latest, ...(banner ? { banner } : {}) }
  if (input.active)
    return { kind: 'redirect', to: tabularConfigBuildPath(input.name, input.active.jobId) }
  return { kind: 'empty' }
}

function bannerForActive(active: TabularConfigBuildDetail | null): ViewBanner | undefined {
  if (!active || !isBuildInFlight(active)) return undefined
  if (!active.result.draft) return { kind: 'building', build: active }
  switch (active.result.phase) {
    case 'awaiting_review':
      return { kind: 'draft_ready', build: active }
    case 'approving':
      return { kind: 'saving', build: active }
    default:
      return { kind: 'building', build: active }
  }
}

function viewForBuild(
  build: TabularConfigBuildDetail,
  versions: IngestMappingConfigSummary[],
  latest: IngestMappingConfigSummary | undefined
): ConfigView {
  const { result } = build
  if (build.state === 'cancelled' || build.state === 'cancelling') {
    return { kind: 'ended', build, outcome: 'stopped' }
  }
  if (result.disposition === 'approved' || result.phase === 'approved') {
    const saved = result.approvedConfig
      ? versions.find((row) => row.version === result.approvedConfig?.version)
      : undefined
    const config = saved ?? latest
    if (!config) return { kind: 'empty' }
    return {
      kind: 'version',
      config,
      latest,
      banner: {
        kind: 'draft_saved',
        build,
        version: result.approvedConfig?.version ?? config.version,
        by: result.approval?.approvedBy ?? 'someone',
        at: build.completedAt ?? build.updatedAt,
        ...(result.exampleIngest ? { exampleIngest: result.exampleIngest } : {})
      }
    }
  }
  if (result.disposition === 'superseded' || result.phase === 'superseded') {
    const shown = versions.find((row) => row.version === result.draft?.baseVersion) ?? latest
    if (!shown) return { kind: 'ended', build, outcome: 'superseded' }
    const { revision } = result
    return {
      kind: 'version',
      config: shown,
      latest,
      banner: {
        kind: 'draft_superseded',
        build,
        by: revision?.revisedBy ?? 'someone',
        at: revision?.revisedAt ?? build.completedAt ?? build.updatedAt,
        note: revision?.note ?? '',
        ...(revision?.supersededBy ? { supersededBy: revision.supersededBy } : {})
      }
    }
  }
  if (result.disposition === 'rejected' || result.phase === 'rejected') {
    const shown = versions.find((row) => row.version === result.draft?.baseVersion) ?? latest
    if (!shown) return { kind: 'ended', build, outcome: 'rejected' }
    return {
      kind: 'version',
      config: shown,
      latest,
      banner: {
        kind: 'draft_rejected',
        build,
        by: result.rejection?.rejectedBy ?? 'someone',
        at: result.rejection?.rejectedAt ?? build.completedAt ?? build.updatedAt,
        note: result.rejection?.note ?? ''
      }
    }
  }
  if (result.phase === 'failed' || build.state === 'error') {
    return { kind: 'ended', build, outcome: 'failed' }
  }
  if ((result.phase === 'awaiting_review' || result.phase === 'approving') && result.draft) {
    const base = versions.find((row) => row.version === result.draft?.baseVersion)
    return {
      kind: 'draft',
      build,
      draft: result.draft,
      ...(base ? { base } : {}),
      ...(result.phase === 'approving' ? { saving: true } : {})
    }
  }
  return { kind: 'building', build }
}

export function parserKindOf(view: ConfigView): 'script' | 'agent' | undefined {
  switch (view.kind) {
    case 'version':
      return view.config.definition.parser.kind
    case 'draft':
      return view.draft.parser.kind
    default:
      return undefined
  }
}

export function tabsFor(parserKind: 'script' | 'agent'): ConfigTab[] {
  return parserKind === 'agent' ? AGENT_TABS : SCRIPT_TABS
}

export function isConfigTab(value: string | null): value is ConfigTab {
  return value !== null && (SCRIPT_TABS as string[]).concat(AGENT_TABS).includes(value)
}

/** The tab to show: the URL's when it exists for this parser kind, else Tables. */
export function defaultTab(parserKind: 'script' | 'agent', requested: string | null): ConfigTab {
  return isConfigTab(requested) && tabsFor(parserKind).includes(requested) ? requested : 'tables'
}

export interface SwitcherOption {
  value: string
  label: string
  /** Where the option leads; the draft option leaves the config route for the build's page. */
  href?: string
}

/**
 * The version switcher's rows: the build in flight first ("draft v2", or
 * "draft v2 · building…" until it has a draft to review), then saved
 * versions newest first, archived ones marked.
 */
export function switcherOptions(input: {
  name: string
  versions: IngestMappingConfigSummary[]
  inFlight: TabularConfigBuildDetail | null
}): SwitcherOption[] {
  const options: SwitcherOption[] = []
  if (input.inFlight && isBuildInFlight(input.inFlight)) {
    const ready = input.inFlight.result.phase === 'awaiting_review' && !!input.inFlight.result.draft
    options.push({
      value: `build:${input.inFlight.jobId}`,
      label: `draft v${input.inFlight.proposedVersion}${ready ? '' : ' · building…'}`,
      href: tabularConfigBuildPath(input.name, input.inFlight.jobId)
    })
  }
  for (const row of newestFirst(input.versions)) {
    options.push({
      value: String(row.version),
      label: `v${row.version}${row.state === 'archived' ? ' · archived' : ''}`
    })
  }
  return options
}
